import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Chip,
  CircularProgress,
  Stack,
  Divider,
  type SelectChangeEvent,
} from '@mui/material';
import { API_URL } from '../config';
import { authedFetch } from '../utils/apiUtils';
import { useSeasonYearContext } from '../context/SeasonYearContext';

interface TablePreview {
  sourceCount: number;
  targetExists: number;
}

interface PreviewData {
  sourceYear: number;
  targetYear: number;
  tables: Record<string, TablePreview>;
}

interface MembershipPreview {
  sourceYear: number;
  targetYear: number;
  sourceMembers: number;
  targetMembers: number;
  alreadyInTarget: number;
  wouldCopy: number;
}

interface CopyResult {
  copied?: number;
  skipped?: boolean;
  reason?: string;
}

const TABLE_LABELS: Record<string, string> = {
  points_date_rules: 'Points Date Rules',
  points_bonus_rules: 'Points Bonus Rules',
  points_division_requirements: 'Division Requirements',
  points_reduction_rules: 'Points Reduction Rules',
  track_assignments: 'Track Assignments',
  division_regions: 'Division Regions',
  series: 'Series',
};

const SeasonSetup: React.FC = () => {
  const { defaultSeasonYear, refreshDefaultYear } = useSeasonYearContext();

  const currentYear = defaultSeasonYear;
  const [sourceYear, setSourceYear] = useState<number>(currentYear);
  const [targetYear, setTargetYear] = useState<number>(currentYear + 1);
  const [activeYear, setActiveYear] = useState<number>(currentYear);
  const [savingActiveYear, setSavingActiveYear] = useState(false);

  const [preview, setPreview] = useState<PreviewData | null>(null);
  const [membershipPreview, setMembershipPreview] = useState<MembershipPreview | null>(null);
  const [copyResults, setCopyResults] = useState<Record<string, CopyResult> | null>(null);
  const [membershipResult, setMembershipResult] = useState<{ membershipsCopied: number } | null>(
    null,
  );

  const [selectedTables, setSelectedTables] = useState<string[]>(Object.keys(TABLE_LABELS));
  const [overwrite, setOverwrite] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmMembershipOpen, setConfirmMembershipOpen] = useState(false);

  const [pendingCount, setPendingCount] = useState(0);
  const loading = pendingCount > 0;
  const beginPending = () => setPendingCount((n) => n + 1);
  const endPending = () => setPendingCount((n) => Math.max(0, n - 1));
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const yearOptions: number[] = [];
  for (let y = currentYear + 2; y >= currentYear - 3; y -= 1) {
    yearOptions.push(y);
  }

  const fetchPreview = async () => {
    beginPending();
    setError(null);
    setPreview(null);
    setCopyResults(null);
    setSuccess(null);
    try {
      const res = await authedFetch(
        `${API_URL}/season-setup/preview?sourceYear=${sourceYear}&targetYear=${targetYear}`,
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to fetch preview');
      }
      const data = await res.json();
      setPreview(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch preview');
    } finally {
      endPending();
    }
  };

  const fetchMembershipPreview = async () => {
    beginPending();
    setError(null);
    setMembershipPreview(null);
    setMembershipResult(null);
    try {
      const res = await authedFetch(
        `${API_URL}/season-setup/preview-memberships?sourceYear=${sourceYear}&targetYear=${targetYear}`,
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to fetch membership preview');
      }
      const data = await res.json();
      setMembershipPreview(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch membership preview');
    } finally {
      endPending();
    }
  };

  const executeCopy = async () => {
    setConfirmOpen(false);
    beginPending();
    setError(null);
    setCopyResults(null);
    setSuccess(null);
    try {
      const res = await authedFetch(`${API_URL}/season-setup/copy`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sourceYear, targetYear, overwrite, tables: selectedTables }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to copy season data');
      }
      const data = await res.json();
      setCopyResults(data.results);
      setSuccess(`Season data copied from ${sourceYear} to ${targetYear}`);
      fetchPreview();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to copy season data');
    } finally {
      endPending();
    }
  };

  const executeMembershipCopy = async () => {
    setConfirmMembershipOpen(false);
    beginPending();
    setError(null);
    setMembershipResult(null);
    try {
      const res = await authedFetch(`${API_URL}/season-setup/copy-memberships`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sourceYear, targetYear }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to copy memberships');
      }
      const data = await res.json();
      setMembershipResult(data);
      setSuccess(`Copied ${data.membershipsCopied} memberships from ${sourceYear} to ${targetYear}`);
      fetchMembershipPreview();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to copy memberships');
    } finally {
      endPending();
    }
  };

  const saveActiveYear = async () => {
    setSavingActiveYear(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await authedFetch(`${API_URL}/config/season-year`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ seasonYear: activeYear }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to update active season year');
      }
      await refreshDefaultYear();
      setSuccess(
        `Active season year updated to ${activeYear}. New sessions will default to ${activeYear}.`,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update active season year');
    } finally {
      setSavingActiveYear(false);
    }
  };

  const toggleTable = (tableName: string) => {
    setSelectedTables((prev) =>
      prev.includes(tableName) ? prev.filter((t) => t !== tableName) : [...prev, tableName],
    );
  };

  const sectionTitleSx = {
    letterSpacing: '0.04em',
    fontWeight: 500,
    color: 'text.secondary',
    display: 'block',
    mb: 1.5,
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1">
          Season Setup
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Set the active season year and roll points rules, track assignments, and memberships
          forward to a new year.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Paper sx={{ p: 2.5, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 2 }}>
          <Box>
            <Typography variant="overline" sx={sectionTitleSx}>
              Active Season Year
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Default year used across the app for new sessions and dropdowns.
            </Typography>
          </Box>
          <Chip
            label={`Currently ${defaultSeasonYear}`}
            color="primary"
            size="small"
            variant="outlined"
            sx={{ fontVariantNumeric: 'tabular-nums' }}
          />
        </Box>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Season Year</InputLabel>
              <Select
                value={activeYear}
                label="Season Year"
                onChange={(e: SelectChangeEvent<number>) => setActiveYear(Number(e.target.value))}
              >
                {yearOptions.map((y) => (
                  <MenuItem key={y} value={y}>
                    {y}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Button
              variant="contained"
              onClick={saveActiveYear}
              disabled={savingActiveYear || activeYear === defaultSeasonYear}
              startIcon={savingActiveYear ? <CircularProgress size={14} color="inherit" /> : null}
            >
              {savingActiveYear ? 'Saving' : 'Set active year'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 2.5, mb: 3 }}>
        <Typography variant="overline" sx={sectionTitleSx}>
          Roll Forward
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Pick a source and target year, then preview to see what would be copied.
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Copy from</InputLabel>
              <Select
                value={sourceYear}
                label="Copy from"
                onChange={(e: SelectChangeEvent<number>) => setSourceYear(Number(e.target.value))}
              >
                {yearOptions.map((y) => (
                  <MenuItem key={y} value={y}>
                    {y}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Copy to</InputLabel>
              <Select
                value={targetYear}
                label="Copy to"
                onChange={(e: SelectChangeEvent<number>) => setTargetYear(Number(e.target.value))}
              >
                {yearOptions.map((y) => (
                  <MenuItem key={y} value={y}>
                    {y}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Button
              variant="contained"
              onClick={() => {
                fetchPreview();
                fetchMembershipPreview();
              }}
              disabled={loading || sourceYear === targetYear}
              startIcon={loading ? <CircularProgress size={14} color="inherit" /> : null}
              fullWidth
            >
              {loading ? 'Loading' : 'Preview'}
            </Button>
          </Grid>
        </Grid>
        {sourceYear === targetYear && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Source and target years must be different.
          </Alert>
        )}
        {targetYear === defaultSeasonYear && sourceYear !== targetYear && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Copying into the active season year. Existing data is preserved unless Overwrite is on.
          </Alert>
        )}
      </Paper>

      {preview && (
        <Paper sx={{ p: 2.5, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1.5 }}>
            <Typography variant="overline" sx={sectionTitleSx}>
              Configuration {sourceYear} → {targetYear}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {selectedTables.length} of {Object.keys(preview.tables).length} selected
            </Typography>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" />
                  <TableCell>Table</TableCell>
                  <TableCell align="right">Source ({sourceYear})</TableCell>
                  <TableCell align="right">Target ({targetYear})</TableCell>
                  <TableCell align="right">Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(preview.tables).map(([tableName, data]) => (
                  <TableRow key={tableName}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={selectedTables.includes(tableName)}
                        onChange={() => toggleTable(tableName)}
                      />
                    </TableCell>
                    <TableCell>{TABLE_LABELS[tableName] || tableName}</TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {data.sourceCount}
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {data.targetExists}
                    </TableCell>
                    <TableCell align="right">
                      {data.targetExists > 0 ? (
                        <Chip label="Has data" color="warning" size="small" variant="outlined" />
                      ) : data.sourceCount > 0 ? (
                        <Chip label="Ready" color="success" size="small" variant="outlined" />
                      ) : (
                        <Chip label="Empty source" size="small" variant="outlined" />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={overwrite && targetYear !== defaultSeasonYear}
                  onChange={(e) => setOverwrite(e.target.checked)}
                  disabled={targetYear === defaultSeasonYear}
                />
              }
              label={
                targetYear === defaultSeasonYear
                  ? `Overwrite disabled (${defaultSeasonYear} is the active year)`
                  : 'Overwrite existing target year data'
              }
            />
            <Box sx={{ flex: 1 }} />
            <Button
              variant="contained"
              color={overwrite ? 'error' : 'primary'}
              onClick={() => setConfirmOpen(true)}
              disabled={loading || selectedTables.length === 0}
            >
              {overwrite ? 'Replace selected tables' : 'Copy selected tables'}
            </Button>
          </Box>
          {overwrite && targetYear !== defaultSeasonYear && (
            <Alert severity="warning" sx={{ mt: 1.5 }}>
              Overwrite is on. Existing {targetYear} data in the selected tables will be deleted
              and replaced.
            </Alert>
          )}

          {copyResults && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Copy results
              </Typography>
              <Stack spacing={0.25}>
                {Object.entries(copyResults).map(([table, result]) => (
                  <Typography key={table} variant="body2">
                    {TABLE_LABELS[table] || table}:{' '}
                    {result.skipped
                      ? `Skipped${result.reason ? ` — ${result.reason}` : ''}`
                      : `${result.copied ?? 0} rows copied`}
                  </Typography>
                ))}
              </Stack>
            </Alert>
          )}
        </Paper>
      )}

      {membershipPreview && (
        <Paper sx={{ p: 2.5, mb: 3 }}>
          <Typography variant="overline" sx={sectionTitleSx}>
            Memberships {sourceYear} → {targetYear}
          </Typography>
          <Grid container spacing={1.5} sx={{ mb: 2 }}>
            {[
              { label: 'Source', value: membershipPreview.sourceMembers },
              { label: 'Already in target', value: membershipPreview.alreadyInTarget },
              {
                label: 'Would copy',
                value: membershipPreview.wouldCopy,
                tone: 'primary' as const,
              },
              {
                label: 'Target after',
                value: membershipPreview.targetMembers + membershipPreview.wouldCopy,
              },
            ].map((stat) => (
              <Grid key={stat.label} item xs={6} md={3}>
                <Box
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 1.5,
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ letterSpacing: '0.04em', fontWeight: 500, textTransform: 'uppercase' }}
                  >
                    {stat.label}
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 600,
                      fontVariantNumeric: 'tabular-nums',
                      color: stat.tone === 'primary' ? 'primary.main' : 'text.primary',
                      lineHeight: 1.2,
                      mt: 0.25,
                    }}
                  >
                    {stat.value.toLocaleString()}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
          <Button
            variant="contained"
            onClick={() => setConfirmMembershipOpen(true)}
            disabled={loading || membershipPreview.wouldCopy === 0}
          >
            Copy {membershipPreview.wouldCopy} memberships
          </Button>

          {membershipResult && (
            <Alert severity="success" sx={{ mt: 2 }}>
              {membershipResult.membershipsCopied} memberships copied to {targetYear}.
            </Alert>
          )}
        </Paper>
      )}

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {overwrite ? 'Replace season data' : 'Copy season data'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Copy {selectedTables.length} {selectedTables.length === 1 ? 'table' : 'tables'} from{' '}
            {sourceYear} to {targetYear}.
            {!overwrite && ' Tables that already have data in the target year will be skipped.'}
          </DialogContentText>
          {overwrite && (
            <Alert severity="error" sx={{ mt: 2 }}>
              Overwrite is on. Existing {targetYear} data in the selected tables will be permanently
              deleted and replaced. This cannot be undone.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setConfirmOpen(false)}>
            Cancel
          </Button>
          <Button onClick={executeCopy} variant="contained" color={overwrite ? 'error' : 'primary'}>
            {overwrite ? 'Delete and replace' : 'Copy'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmMembershipOpen}
        onClose={() => setConfirmMembershipOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Copy memberships</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Copy {membershipPreview?.wouldCopy ?? 0} driver memberships from {sourceYear} to{' '}
            {targetYear}. Existing memberships in {targetYear} are not affected.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setConfirmMembershipOpen(false)}>
            Cancel
          </Button>
          <Button onClick={executeMembershipCopy} variant="contained">
            Copy
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SeasonSetup;
